import { useState, useEffect, useCallback } from 'react';
import { createCiferSdk, keyManagement } from 'cifer-sdk';

const BLACKBOX_URL = 'https://cifer-blackbox.ternoa.dev:3010';
const CHAIN_ID = 1; // ETH mainnet

export interface OnChainCifer {
  secretId: bigint;
  owner: string;
  delegate: string;
  isSyncing: boolean;
  secretType: number;
  publicKeyCid: string;
}

interface UseCiferOnChainReturn {
  cifers: OnChainCifer[];
  dataUsage: null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

let sdkPromise: ReturnType<typeof createCiferSdk> | null = null;

function getSdkPromise() {
  if (!sdkPromise) {
    sdkPromise = createCiferSdk({ blackboxUrl: BLACKBOX_URL });
  }
  return sdkPromise;
}

export function useCiferOnChain(address: string | null): UseCiferOnChainReturn {
  const [cifers, setCifers] = useState<OnChainCifer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCifers = useCallback(async () => {
    if (!address) return;

    setLoading(true);
    setError(null);

    try {
      const sdk = await getSdkPromise();
      const controllerAddress = sdk.getControllerAddress(CHAIN_ID);
      const params = {
        chainId: CHAIN_ID,
        controllerAddress,
        readClient: sdk.readClient,
      };

      // Get all secret IDs owned by this wallet
      const secrets = await keyManagement.getSecretsByWallet(params, address as `0x${string}`);
      const ownedIds = secrets.owned;

      // Fetch details for each secret
      const details = await Promise.all(
        ownedIds.map(async (secretId) => {
          try {
            const state = await keyManagement.getSecret(params, secretId);
            return {
              secretId,
              owner: state.owner,
              delegate: state.delegate,
              isSyncing: state.isSyncing,
              secretType: state.secretType,
              publicKeyCid: state.publicKeyCid,
            } as OnChainCifer;
          } catch {
            return null;
          }
        })
      );

      setCifers(details.filter((d): d is OnChainCifer => d !== null));
    } catch (err: any) {
      setError(err?.message || 'Failed to load CIFERs');
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    fetchCifers();
  }, [fetchCifers]);

  return { cifers, dataUsage: null, loading, error, refresh: fetchCifers };
}
